import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import { CookieConsent } from "./components/CookieConsent";
import { ComparisonProvider } from "./contexts/ComparisonContext";
import ComparisonBar from "./components/ComparisonBar";

// Lazy load pages for better performance
const Gallery = lazy(() => import("./pages/Gallery"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const ArtAnalysis = lazy(() => import("./pages/ArtAnalysis"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Search = lazy(() => import("./pages/Search"));
const Premium = lazy(() => import("./pages/Premium"));
const Favorites = lazy(() => import("./pages/Favorites"));
const SellerDashboard = lazy(() => import("./pages/SellerDashboard"));
const AdminVerification = lazy(() => import("./pages/AdminVerification"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ArtworkManagement = lazy(() => import("./pages/ArtworkManagement"));
const PromotionCodeManager = lazy(() => import("./pages/PromotionCodeManager"));
const Chat = lazy(() => import("./pages/Chat"));
const PaymentConfirm = lazy(() => import("./pages/PaymentConfirm"));
const AuctionHub = lazy(() => import("./pages/AuctionHub"));
const Auction = lazy(() => import("./pages/Auction"));
const ExclusiveAuction = lazy(() => import("./pages/ExclusiveAuction"));
const ArtAuction = lazy(() => import("./pages/ArtAuction"));
const ArtNews = lazy(() => import("./pages/ArtNews"));
const AboutArtExperto = lazy(() => import("./pages/AboutArtExperto"));
const ArtPortfolioValue = lazy(() => import("./pages/ArtPortfolioValue"));
const Purchases = lazy(() => import("./pages/Purchases"));
const BuyerDashboard = lazy(() => import("./pages/BuyerDashboard"));
const ArtworkComparison = lazy(() => import("./pages/ArtworkComparison"));
const Forum = lazy(() => import("./pages/Forum"));
const ForumPost = lazy(() => import("./pages/ForumPost"));
const Network = lazy(() => import("./pages/Network"));
const CreateArticle = lazy(() => import("./pages/CreateArticle"));
const ArticleHistory = lazy(() => import("./pages/ArticleHistory"));
const Referrals = lazy(() => import("./pages/Referrals"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ComparisonProvider>
        <Toaster />
        <Sonner />
        <CookieConsent />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/art-analysis" element={<ArtAnalysis />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/search" element={<Search />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/dashboard" element={<SellerDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/verification" element={<AdminVerification />} />
              <Route path="/my-artworks" element={<ArtworkManagement />} />
              <Route path="/admin/promo-codes" element={<PromotionCodeManager />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/payment-confirm" element={<PaymentConfirm />} />
              <Route path="/auction-hub" element={<AuctionHub />} />
              <Route path="/auction/:id" element={<Auction />} />
              <Route path="/exclusive-auction/:id" element={<ExclusiveAuction />} />
              <Route path="/art-auction" element={<ArtAuction />} />
              <Route path="/art-news" element={<ArtNews />} />
              <Route path="/about" element={<AboutArtExperto />} />
              <Route path="/art-portfolio-value" element={<ArtPortfolioValue />} />
              <Route path="/purchases" element={<Purchases />} />
              <Route path="/buyer-dashboard" element={<BuyerDashboard />} />
              <Route path="/compare" element={<ArtworkComparison />} />
              <Route path="/forum" element={<Forum />} />
              <Route path="/forum/:id" element={<ForumPost />} />
              <Route path="/network" element={<Network />} />
              <Route path="/create-article" element={<CreateArticle />} />
              <Route path="/my-articles" element={<ArticleHistory />} />
              <Route path="/referrals" element={<Referrals />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <ComparisonBar />
          </Suspense>
        </BrowserRouter>
      </ComparisonProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
